import { useEffect, useRef, useState } from "react";
import { http } from "@/lib/api";
import { toPng } from "html-to-image";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sun, Moon, Wind, Drop, Thermometer, MapPin, Share, Download, ArrowsClockwise } from "@phosphor-icons/react";

const LOCATIONS = [
  { name: "New Delhi", lat: 28.6139, lon: 77.2090 },
  { name: "Mumbai", lat: 19.0760, lon: 72.8777 },
  { name: "London", lat: 51.5074, lon: -0.1278 },
  { name: "New York", lat: 40.7128, lon: -74.0060 },
  { name: "Tokyo", lat: 35.6762, lon: 139.6503 },
  { name: "Sydney", lat: -33.8688, lon: 151.2093 },
  { name: "Reykjavik", lat: 64.1466, lon: -21.9426 },
  { name: "Nairobi", lat: -1.2921, lon: 36.8219 },
];

function aqiLabel(v) {
  if (v == null) return "—";
  if (v <= 50) return "Good";
  if (v <= 100) return "Moderate";
  if (v <= 150) return "Unhealthy SG";
  if (v <= 200) return "Unhealthy";
  if (v <= 300) return "Very Unhealthy";
  return "Hazardous";
}

export default function RegionCard() {
  const [i, setI] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);
  const loc = LOCATIONS[i];

  const load = () => {
    setLoading(true);
    http.get(`/region-snapshot?lat=${loc.lat}&lon=${loc.lon}&name=${encodeURIComponent(loc.name)}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };
  useEffect(load, [i]); // eslint-disable-line

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not available");
    setLoading(true);
    navigator.geolocation.getCurrentPosition(async pos => {
      const { latitude, longitude } = pos.coords;
      const { data } = await http.get(`/region-snapshot?lat=${latitude}&lon=${longitude}&name=Your%20Location`);
      setData(data);
      setLoading(false);
    }, () => { toast.error("Location denied"); setLoading(false); }, { timeout: 8000 });
  };

  const downloadPng = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#FFF6E5" });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `naturepulse-${(data?.location || "region").toLowerCase().replace(/\s+/g, "-")}.png`;
      a.click();
      toast.success("Card saved.");
    } catch (e) { toast.error("Could not export PNG"); }
    finally { setBusy(false); }
  };

  const shareCard = async () => {
    const text = `${data?.location} · ${data?.temperature_c != null ? Math.round(data.temperature_c) + "°C" : "—"} · AQI ${data?.aqi ?? "—"} · +${data?.temp_anomaly_c}°C anomaly — NaturePulse`;
    const url = window.location.origin + `/?region=${encodeURIComponent(data?.location || "")}`;
    if (navigator.share) {
      try { await navigator.share({ title: "NaturePulse — my region", text, url }); }
      catch {}
    } else {
      await navigator.clipboard.writeText(`${text} — ${url}`);
      toast.success("Link copied to clipboard.");
    }
  };

  const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—";

  return (
    <section data-testid="region-card-section" className="py-24 md:py-32 px-6 md:px-10 max-w-[1600px] mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
        <div className="md:col-span-5">
          <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)] mb-4">/ State of your region</div>
          <h2 className="font-serif-display text-5xl md:text-7xl leading-[0.95] tracking-tighter">
            The planet, <em className="text-[color:var(--np-brand)]">where you stand.</em>
          </h2>
        </div>
        <div className="md:col-span-6 md:col-start-7 self-end">
          <p className="text-[color:var(--np-text-2)] text-lg mb-6">
            A one-glance card summarising the temperature, air quality, temperature anomaly, and closest civic hazard for any place on Earth. Share it, save it, print it.
          </p>
          <div className="flex flex-wrap gap-2">
            {LOCATIONS.map((l, idx) => (
              <button
                key={l.name}
                onClick={() => setI(idx)}
                data-testid={`region-loc-${idx}`}
                className={`font-mono-tech text-[10px] tracking-[0.3em] uppercase px-3 py-2 border transition-colors ${
                  idx === i ? "bg-[color:var(--np-text)] text-[color:var(--np-bg)] border-[color:var(--np-text)]"
                            : "border-black/20 hover:border-[color:var(--np-text)]"
                }`}
              >{l.name}</button>
            ))}
            <button
              onClick={useMyLocation}
              data-testid="region-my-location-btn"
              className="font-mono-tech text-[10px] tracking-[0.3em] uppercase px-3 py-2 border border-[color:var(--np-brand)] text-[color:var(--np-brand)] hover:bg-[color:var(--np-brand)] hover:text-[color:var(--np-bg)] transition-colors inline-flex items-center gap-2"
            >
              <MapPin size={12} weight="bold" /> My location
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
        {/* The card (exported to PNG) */}
        <div
          ref={cardRef}
          className="lg:col-span-2 relative overflow-hidden"
          data-testid="region-card"
          style={{
            background: "linear-gradient(135deg, #FFE0B0 0%, #F9A96D 45%, #E4572E 80%, #7A2E1A 100%)",
            padding: "48px",
            minHeight: 520,
            color: "#241209",
            fontFamily: "Manrope, sans-serif",
          }}
        >
          {/* corner ornaments inside the card */}
          <svg width="120" height="120" viewBox="0 0 120 120" style={{ position: "absolute", top: -20, right: -20, opacity: 0.85 }}>
            <defs>
              <radialGradient id="rc-sun" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#FFF4C4" />
                <stop offset="70%" stopColor="#F5B342" />
                <stop offset="100%" stopColor="#EA580C" />
              </radialGradient>
            </defs>
            <circle cx="60" cy="60" r="34" fill="url(#rc-sun)" />
            {Array.from({ length: 12 }).map((_, k) => {
              const angle = (k * 30 * Math.PI) / 180;
              return <line key={k} x1={60 + Math.cos(angle) * 42} y1={60 + Math.sin(angle) * 42} x2={60 + Math.cos(angle) * 56} y2={60 + Math.sin(angle) * 56} stroke="#F5B342" strokeWidth="2" strokeLinecap="round" opacity="0.75" />;
            })}
          </svg>
          <svg width="100" height="100" viewBox="0 0 100 100" style={{ position: "absolute", bottom: -10, left: -10, opacity: 0.7 }}>
            <defs>
              <mask id="rc-cres"><rect width="100" height="100" fill="white" /><circle cx="70" cy="42" r="34" fill="black" /></mask>
            </defs>
            <circle cx="50" cy="50" r="38" fill="#F5E6D3" mask="url(#rc-cres)" />
          </svg>

          <div style={{ position: "relative", zIndex: 2 }}>
            <div style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 10, letterSpacing: "0.4em", textTransform: "uppercase", color: "#4A1F0E", marginBottom: 12 }}>
              NATUREPULSE · STATE OF THE REGION
            </div>
            <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 64, fontWeight: 400, letterSpacing: "-0.02em", lineHeight: 0.95, color: "#241209" }}>
              {loading ? "…" : data?.location}
            </div>
            <div style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 11, letterSpacing: "0.25em", textTransform: "uppercase", color: "#4A1F0E", marginTop: 8 }}>
              {loc.lat.toFixed(2)}°{loc.lat >= 0 ? "N" : "S"} · {Math.abs(loc.lon).toFixed(2)}°{loc.lon >= 0 ? "E" : "W"} · {data?.timestamp ? new Date(data.timestamp).toISOString().slice(0, 10) : ""}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginTop: 40 }}>
              <div>
                <div style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", color: "#4A1F0E" }}>Temperature</div>
                <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 88, lineHeight: 1, color: "#241209", marginTop: 4 }}>
                  {data?.temperature_c != null ? Math.round(data.temperature_c) + "°" : "—"}
                </div>
                <div style={{ fontFamily: "Manrope, sans-serif", fontSize: 13, color: "#4A1F0E" }}>
                  Feels {data?.feels_like_c != null ? Math.round(data.feels_like_c) + "°" : "—"} · Hi {data?.high_c != null ? Math.round(data.high_c) + "°" : "—"} · Lo {data?.low_c != null ? Math.round(data.low_c) + "°" : "—"}
                </div>
              </div>
              <div>
                <div style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", color: "#4A1F0E" }}>Air Quality</div>
                <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 88, lineHeight: 1, color: "#241209", marginTop: 4 }}>
                  {data?.aqi ?? "—"}
                </div>
                <div style={{ fontFamily: "Manrope, sans-serif", fontSize: 13, color: "#4A1F0E" }}>{aqiLabel(data?.aqi)} · PM2.5 {data?.pm25 != null ? data.pm25.toFixed(1) : "—"}</div>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginTop: 32, paddingTop: 24, borderTop: "1px solid rgba(36,18,9,0.2)" }}>
              <Stat label="Wind" value={data?.wind_kmh != null ? Math.round(data.wind_kmh) + " km/h" : "—"} />
              <Stat label="Humidity" value={data?.humidity != null ? data.humidity + "%" : "—"} />
              <Stat label="Sunrise" value={fmtTime(data?.sunrise)} />
              <Stat label="Sunset" value={fmtTime(data?.sunset)} />
            </div>

            <div style={{ marginTop: 32, padding: 20, background: "rgba(36,18,9,0.08)", border: "1px solid rgba(36,18,9,0.15)" }}>
              <div style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", color: "#7A2E1A", marginBottom: 6 }}>
                Regional temperature anomaly · illustrative
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
                <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 52, lineHeight: 1, color: "#7A2E1A" }}>
                  +{data?.temp_anomaly_c ?? "—"}°C
                </div>
                <div style={{ fontFamily: "Manrope, sans-serif", fontSize: 13, color: "#4A1F0E" }}>vs. 1850–1900 baseline · WMO methodology</div>
              </div>
            </div>

            {data?.nearest_hazard && (
              <div style={{ marginTop: 20, padding: 20, background: "rgba(36,18,9,0.08)", border: "1px solid rgba(36,18,9,0.15)" }}>
                <div style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", color: "#7A2E1A", marginBottom: 6 }}>
                  Nearest civic hazard · {Math.round(data.nearest_hazard.distance_km)} km
                </div>
                <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 28, lineHeight: 1.05, color: "#241209", textTransform: "capitalize" }}>
                  {data.nearest_hazard.hazard.replace("-", " ")} · {data.nearest_hazard.severity}
                </div>
                <div style={{ fontFamily: "Manrope, sans-serif", fontSize: 13, color: "#4A1F0E", marginTop: 6 }}>
                  {data.nearest_hazard.location} — {data.nearest_hazard.description}
                </div>
              </div>
            )}

            <div style={{ marginTop: 32, display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "JetBrains Mono, monospace", fontSize: 10, letterSpacing: "0.35em", textTransform: "uppercase", color: "#4A1F0E" }}>
              <span>naturepulse · a socialpulse archive</span>
              <span>every observation matters</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-4" data-testid="region-card-actions">
          <ActionButton icon={<Download size={16} weight="bold" />} label={busy ? "Exporting…" : "Download PNG"} onClick={downloadPng} testid="region-download-btn" disabled={busy || loading} primary />
          <ActionButton icon={<Share size={16} weight="bold" />} label="Share link" onClick={shareCard} testid="region-share-btn" disabled={loading} />
          <ActionButton icon={<ArrowsClockwise size={16} weight="bold" />} label="Refresh" onClick={load} testid="region-refresh-btn" disabled={loading} />
          <div className="glass p-4 text-sm text-[color:var(--np-text-2)]">
            <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted)] mb-2">Data</div>
            Weather & AQI · Open‑Meteo · <br />
            Temp anomaly baseline · WMO 1850–1900 · <br />
            Hazards · Community + agency reports
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 9, letterSpacing: "0.3em", textTransform: "uppercase", color: "#7A2E1A" }}>{label}</div>
      <div style={{ fontFamily: "Manrope, sans-serif", fontSize: 18, color: "#241209", marginTop: 4 }}>{value}</div>
    </div>
  );
}

function ActionButton({ icon, label, onClick, testid, disabled, primary }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      disabled={disabled}
      className={`w-full flex items-center justify-between font-mono-tech text-[11px] tracking-[0.3em] uppercase px-5 py-4 border transition-colors ${
        primary
          ? "bg-[color:var(--np-text)] text-[color:var(--np-bg)] border-[color:var(--np-text)] hover:bg-[color:var(--np-brand)] hover:border-[color:var(--np-brand)]"
          : "border-black/20 hover:bg-[color:var(--np-text)] hover:text-[color:var(--np-bg)] hover:border-[color:var(--np-text)]"
      } disabled:opacity-50`}
    >
      <span className="flex items-center gap-3">{icon}{label}</span>
    </button>
  );
}
